import { postFetcher } from "@xtribe/shared/lib/defaultFetcher";
import { Stripe } from "@stripe/stripe-js";
import { handleErrorCode } from "./handleErrorCode";

interface HandlePaymentResultProps {
  result: any;
  stripe: Stripe;
  paymentMethod: string;
}

interface HandleCardRequiresActionProps {
  clientSecret: string;
  paymentMethod: string;
  stripe: Stripe;
}

interface HandleUpdateOrderProps {
  teamId: string;
  selectedProduct?: string | null;
  selectedLesson?: string | null;
  invoiceId?: string;
  invoiceUrl?: string;
  access?: string;
  status?: string;
  platform?: string;
}

export const handleCardRequiresAction = async ({
  clientSecret,
  paymentMethod,
  stripe,
}: HandleCardRequiresActionProps) => {
  try {
    const action = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethod,
    });
    if (action.error) {
      throw new Error(handleErrorCode(action.error));
    } else {
      return action.paymentIntent;
    }
  } catch (error) {
    throw new Error(
      error.message ?? "Es gab einen Fehler beim Verifizieren deiner Zahlung"
    );
  }
};

export const handlePaymentResult = async ({
  result,
  stripe,
  paymentMethod,
}: HandlePaymentResultProps) => {
  let paymentIntent;
  if (!result?.payment_intent) {
    throw new Error("Es gab einen Fehler bei deiner Zahlung");
  } else if (result?.payment_intent?.status === "requires_action") {
    try {
      paymentIntent = await handleCardRequiresAction({
        clientSecret: result.payment_intent.client_secret,
        paymentMethod,
        stripe,
      });
      result.payment_intent = paymentIntent;
    } catch (error) {
      throw new Error(
        error.message ?? "Es gab einen Fehler beim Verifizieren deiner Zahlung"
      );
    }
  } else if (result.payment_intent.status === "requires_payment_method") {
    throw new Error(
      "Deine Zahlungsmethode wurde abgelehnt. Füge zuerst eine neue Zahlungsmethode hinzu, bevor du die Zahlung erneut versuchst."
    );
  } else if (result.payment_intent?.last_payment_error) {
    throw new Error(handleErrorCode(result.payment_intent.last_payment_error));
  }
  if (
    result?.payment_intent?.status === "succeeded" ||
    result?.payment_intent?.status === "processing"
  ) {
    let message = "";
    if (result?.payment_intent?.status === "processing") {
      message =
        "Deine Zahlung wurde erfolgreich eingeleitet. Du wirst in kürze weitergeleitet.";
    } else {
      message =
        "Deine Zahlung war erfolgreich. Du wirst in kürze weitergeleitet.";
    }
    return {
      result: {
        ...result,
        payment_intent: paymentIntent ?? result.payment_intent,
      },
      success: true,
      message,
    };
  } else {
    throw new Error("Es gab einen Fehler bei deiner Zahlung");
  }
};

export const onUpdateOrder = async ({
  selectedProduct,
  selectedLesson,
  teamId,
  invoiceId,
  invoiceUrl,
  access,
  status,
  platform,
}: HandleUpdateOrderProps) => {
  try {
    let updatedOrder;
    if (selectedProduct) {
      updatedOrder = await postFetcher(
        `/api/v0/orders/products/${selectedProduct}?teamId=${teamId}`,
        {
          status,
          platform,
          invoiceId,
          access,
          invoiceUrl,
        }
      );
    } else if (selectedLesson) {
      updatedOrder = await postFetcher(
        `/api/v0/orders/products/${selectedProduct}?teamId=${teamId}`,
        {
          status,
          platform,
          invoiceId,
          access,
          invoiceUrl,
        }
      );
    } else {
      throw new Error(
        "Es gab einen Fehler beim Aktualisieren deiner Bestellung"
      );
    }
    return updatedOrder;
  } catch (error) {
    throw new Error("Es gab einen Fehler beim Aktualisieren deiner Bestellung");
  }
};
